import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileText, Download, Trash2 } from "lucide-react";
import { useCanDelete } from "@/context/AuthContext";

const DOC_TYPES = ["rc", "insurance", "permit", "fitness", "puc", "calibration", "peso", "aadhaar", "license", "gst", "pan", "contract", "other"];

export default function Documents() {
  const qc = useQueryClient();
  const canDelete = useCanDelete();
  const [entityType, setEntityType] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ entity_type: "bowser", entity_id: "", doc_type: "insurance", name: "", file_url: "", expiry_date: "", notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const params = entityType && entityType !== "all" ? `?entity_type=${entityType}` : "";
  const { data } = useQuery({ queryKey: ["documents", entityType], queryFn: () => api.get(`/documents${params}`).then((r) => r.data) });
  const { data: bowsers } = useQuery({ queryKey: ["fleet-bowsers-doc"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data) });
  const { data: drivers } = useQuery({ queryKey: ["fleet-drivers-doc"], queryFn: () => api.get("/fleet/drivers").then((r) => r.data) });
  const { data: customers } = useQuery({ queryKey: ["cust-doc"], queryFn: () => api.get("/customers?limit=200").then((r) => r.data) });

  const create = useMutation({
    mutationFn: (payload) => api.post("/documents", payload),
    onSuccess: () => { toast.success("Document added"); setOpen(false); qc.invalidateQueries({ queryKey: ["documents"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/documents/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["documents"] }); },
  });

  const entityOptions = form.entity_type === "bowser" ? (bowsers?.items || []).map((b) => ({ id: b.id, label: `${b.code} · ${b.registration_no}` }))
    : form.entity_type === "driver" ? (drivers?.items || []).map((d) => ({ id: d.id, label: `${d.code} · ${d.name}` }))
    : form.entity_type === "customer" ? (customers?.items || []).map((c) => ({ id: c.id, label: `${c.code} · ${c.name}` }))
    : [];

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, file_url: reader.result, name: f.name || file.name }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Document Vault</h1>
          <p className="text-sm text-muted-foreground">RC, insurance, permits, PESO certificates & KYC docs.</p>
        </div>
        <div className="flex gap-2">
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="w-40" data-testid="doc-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              <SelectItem value="bowser">Bowsers</SelectItem>
              <SelectItem value="driver">Drivers</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="company">Company</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="btn-add-doc"><Plus size={14} className="mr-1" /> Add document</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Entity type</Label>
                  <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v, entity_id: "" })}>
                    <SelectTrigger data-testid="doc-entity-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bowser">Bowser</SelectItem>
                      <SelectItem value="driver">Driver</SelectItem>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="company">Company</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {entityOptions.length > 0 && form.entity_type !== "company" && (
                  <div>
                    <Label>Entity</Label>
                    <Select value={form.entity_id} onValueChange={(v) => setForm({ ...form, entity_id: v })}>
                      <SelectTrigger data-testid="doc-entity-id"><SelectValue placeholder="Pick entity" /></SelectTrigger>
                      <SelectContent>{entityOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Document type</Label>
                  <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                    <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Document name</Label><Input value={form.name} onChange={set("name")} data-testid="doc-name" /></div>
                <div><Label>File</Label><Input type="file" accept="image/*,.pdf" onChange={onFile} data-testid="doc-file" /></div>
                <div><Label>Expiry date (if applicable)</Label><Input type="date" value={form.expiry_date} onChange={set("expiry_date")} /></div>
                <div><Label>Notes</Label><Input value={form.notes} onChange={set("notes")} /></div>
              </div>
              <DialogFooter>
                <Button onClick={() => create.mutate(form)}
                  disabled={!form.name || !form.file_url || (form.entity_type !== "company" && !form.entity_id)}
                  data-testid="btn-save-doc"
                >Upload</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">Expiry</th>
                <th className="px-4 py-3">Uploaded</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((d) => (
                <tr key={d.id} className="border-b border-border/40 text-sm">
                  <td className="px-4 py-3"><span className="rounded border border-border bg-background px-2 py-0.5 text-xs font-bold uppercase text-primary">{d.doc_type}</span></td>
                  <td className="px-4 py-3 font-semibold flex items-center gap-2"><FileText size={14} className="text-muted-foreground" /> {d.name}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{d.entity_type} · {d.entity_id.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-xs">{d.expiry_date || "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString()} · {d.uploaded_by}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <a href={d.file_url} target="_blank" rel="noreferrer" className="rounded border border-border p-1.5 hover:border-primary" title="Download"><Download size={12} /></a>
                      {canDelete && (
                        <button onClick={() => del.mutate(d.id)} className="rounded border border-border p-1.5 text-status-cancelled hover:border-status-cancelled" title="Delete" data-testid={`doc-del-${d.id}`}><Trash2 size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">No documents yet. Click &ldquo;Add document&rdquo; to upload.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
